import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  BreadcrumbsProps,
  ButtonBase,
  Menu,
  MenuItem,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { ISubTopic } from '../../store/SubTopicSlice';
import { ITopic } from '../../store/TopicSlice';
import { TP_COLORS } from '../../constants';
import { getUrlConvertedString } from '../../helper';

export type BreadcrumbOption = {
  type: 'text' | 'link';
  title: string;
  link?: string;
  highlight?: boolean;
};

export type TBreadcrumbsProps = {
  componentProps?: BreadcrumbsProps;
  options: Array<BreadcrumbOption>;
  showDropDown?: boolean;
  textColor?: string;
  highlightColor?: string;
};

const defaultHomePageBreadcrumbOption: BreadcrumbOption = {
  type: 'link',
  title: 'Home',
  link: `${process.env.PUBLIC_URL}`,
};

export const getBreadCrumbOptionsByTopicAndSubTopicData = (
  topic?: ITopic,
  subTopic?: ISubTopic,
  includeHomeOption = true
): Array<BreadcrumbOption> => {
  const options: Array<BreadcrumbOption> = [];

  if (includeHomeOption) {
    options.push(defaultHomePageBreadcrumbOption);
  }

  if (topic) {
    const urlConvertedCurriculum = getUrlConvertedString(topic.curriculum);
    const urlConvertedSubject = getUrlConvertedString(topic.subject);
    const urlConvertedSubjectLevel = getUrlConvertedString(topic.subjectLevel);
    const urlConvertedSubjectCode = getUrlConvertedString(topic.subjectCode);
    const urlConvertedTopic = getUrlConvertedString(topic.name);

    options.push({
      type: 'link',
      title: topic.curriculum,
      link: `${process.env.PUBLIC_URL}/${urlConvertedCurriculum}`,
    });

    options.push({
      type: 'link',
      title: topic.subject,
      link: `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedSubjectLevel}/${urlConvertedSubjectCode}`,
    });

    options.push({
      type: topic.isAssessment ? 'text' : 'link',
      title: topic.name,
      link: `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedSubjectLevel}/${urlConvertedSubjectCode}/${urlConvertedTopic}/${topic._id}`,
    });

    if (subTopic) {
      const urlConvertedSubTopic = getUrlConvertedString(subTopic.name);

      options.push({
        type:
          topic.isAssessment ||
          !(subTopic.resources && subTopic.resources.length)
            ? 'text'
            : 'link',
        title: subTopic.name,
        link: `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopic._id}`,
      });
    }
  }

  return options;
};

const defaultStyles: React.CSSProperties = {
  color: TP_COLORS.white,
  fontWeight: 500,
};

const Breadcrumbs: React.FC<TBreadcrumbsProps> = ({
  options = [],
  componentProps = {},
  showDropDown = false,
  textColor = TP_COLORS.white,
  highlightColor,
}) => {
  const navigate = useNavigate();

  const dropdownEnabled = showDropDown && options.length > 1;

  let lastOption: BreadcrumbOption | undefined;

  if (dropdownEnabled && options.length > 1) {
    lastOption = options[options.length - 1];
  }

  // State
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isDropdownOpen = Boolean(anchorEl);

  const openDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeDropdown = () => {
    setAnchorEl(null);
  };

  const renderOption = (option: BreadcrumbOption, index: number) => {
    let _textColor = textColor;

    if (option.highlight) {
      _textColor = '#01A79F';

      if (highlightColor) {
        _textColor = highlightColor;
      }
    }

    if (option.type === 'link' && option.link) {
      return (
        <NavLink
          key={`bc-link-option-${index + 1}`}
          to={option.link}
          style={{ color: textColor, textDecoration: 'none' }}
        >
          <Typography
            color="inherit"
            sx={{
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {option.title}
          </Typography>
        </NavLink>
      );
    }

    return (
      <Typography
        key={`bc-txt-option-${index + 1}`}
        fontWeight={componentProps.style?.fontWeight}
        color={_textColor}
      >
        {option.title}
      </Typography>
    );
  };

  return (
    <MuiBreadcrumbs
      aria-label="breadcrumb"
      separator={<ChevronRightIcon />}
      {...componentProps}
      style={{
        ...defaultStyles,
        ...(componentProps.style || {}),
        color: textColor,
      }}
    >
      {!dropdownEnabled
        ? options.map((option, index) => {
            return renderOption(option, index);
          })
        : null}
      {dropdownEnabled ? (
        <>
          <ButtonBase
            id="bc-dd-btn"
            onClick={openDropdown}
            style={{
              backgroundColor: '#f5f5f5',
              color: TP_COLORS.black,
              borderRadius: '2px',
              padding: '0',
            }}
          >
            <MoreHorizIcon style={{ width: '24px', height: '16px' }} />
          </ButtonBase>
          <Menu
            id="bc-menu"
            anchorEl={anchorEl}
            open={isDropdownOpen}
            onClose={closeDropdown}
            MenuListProps={{
              'aria-labelledby': 'bc-dd-btn',
            }}
          >
            {options
              .filter((item) => lastOption && item.title !== lastOption.title)
              .map((item, index) => {
                if (item.type === 'link') {
                  return (
                    <MenuItem
                      key={`ddm-${index}`}
                      onClick={() => {
                        if (item.link) {
                          navigate(item.link);
                        }

                        closeDropdown();
                      }}
                    >
                      {item.title}
                    </MenuItem>
                  );
                }

                return (
                  <MenuItem
                    key={`ddm-${index}`}
                    onClick={() => {
                      closeDropdown();
                    }}
                  >
                    {item.title}
                  </MenuItem>
                );
              })}
          </Menu>
        </>
      ) : null}
      {dropdownEnabled && lastOption ? renderOption(lastOption, 1) : null}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
